import { useDisclosure } from '@chakra-ui/react';
import React from 'react';
import {
  Dropdown,
  DropdownContent,
  DropdownDivider,
  IconButton,
  MoreIcon,
  useSnackbar,
} from 'ui-components';
import { SavedList } from '~/clientApi/savedLists.model';
import {
  useCreateList,
  useUpdateListCreators,
} from '~/clientApi/whalarModeLists';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import EditListModal from '~/screens/WhalarModeListsScreen/components/EditListModal';
import { AddToListModal } from '~/screens/WhalarModeTalentRecord/components/AddToListModal';
import { useCreatorCopyEmbed } from '~/screens/WhalarModeTalentRecord/hooks/useCreatorCopyEmbed';
import { ICreator } from '~/types';

import { AddNoteDropdownItem } from './AddNoteDropdownItem';
import { AddToListDropdownItem } from './AddToListDropdownItem';
import { CopyEmbedDropdownItem } from './CopyEmbedDropdownItem';
import { ManagerDropdown } from './ManagerDropdown';
import { RemoveCreatorDropdownItem } from './RemoveCreatorDropdownItem';

interface ListCreatorDropdownMenuProps {
  list: SavedList;
  creator: ICreator;
  isShared?: boolean | undefined;
  note?: string;
  onNoteOpen?: () => void;
}

// Copied and adapted from components/TalentDropdownMenu/RosterViewDropdownMenu.tsx
export function ListCreatorDropdownMenu({
  list,
  creator,
  isShared,
  note,
  onNoteOpen,
}: ListCreatorDropdownMenuProps) {
  const { onBasicClick, onDetailClick } = useCreatorCopyEmbed(creator);
  const snackbar = useSnackbar();
  const {
    isOpen: isAddCreatorToListModalOpen,
    onClose: onAddCreatorToListModalClose,
    onOpen: onAddCreatorToListModalOpen,
  } = useDisclosure();

  const {
    isOpen: isOpenCreateList,
    onOpen: onOpenCreateList,
    onClose: onCloseCreateList,
  } = useDisclosure();

  const { mutateAsync: mutateAsyncCreators } = useUpdateListCreators();
  const creationMutation = useCreateList();

  const handleAddToList = async (list: SavedList) => {
    try {
      await mutateAsyncCreators({ id: list.id, addCreatorIds: [creator.id] });
      snackbar.success({
        description: 'List updated: The list has been updated',
        duration: 9000,
        isCloseable: true,
      });
    } catch {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
    }
  };

  const handleRemoveFromList = async () => {
    try {
      await mutateAsyncCreators({
        id: list.id,
        removeCreatorIds: [creator.id],
      });
      snackbar.success({
        description: 'List updated: The list has been updated',
        duration: 9000,
        isCloseable: true,
      });
    } catch {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
    }
  };

  const handleCreateList = ({
    listName,
  }: {
    id?: string;
    listName: string;
  }) => {
    creationMutation.mutate(
      { name: listName },
      {
        onSuccess: async (result) => {
          await handleAddToList(result.data);
        },
      },
    );
  };

  return (
    <>
      <AddToListModal
        creators={[creator]}
        isOpen={isAddCreatorToListModalOpen}
        onClose={onAddCreatorToListModalClose}
        onItemClick={handleAddToList}
        onCreateListOpen={onOpenCreateList}
      />
      <EditListModal
        isOpen={isOpenCreateList}
        onClose={onCloseCreateList}
        onEdit={handleCreateList}
      />

      <Dropdown>
        {({ isOpen, buttonRef, getReferenceProps }) => (
          <>
            <IconButton
              aria-label="Open creator invitation actions"
              icon={<MoreIcon boxSize={4} />}
              isActive={isOpen}
              ref={buttonRef}
              size="md"
              variant="transparent"
              {...getReferenceProps()}
            />

            <DropdownContent
              __css={{
                // Required to fix my agency header z-index issue
                zIndex: 1099,
              }}
            >
              <CopyEmbedDropdownItem
                creator={creator}
                onBasicCopy={onBasicClick}
                onDetailCopy={onDetailClick}
              />
              <DropdownDivider />

              <AddToListDropdownItem onClick={onAddCreatorToListModalOpen} />

              {creator.manager && (
                <ManagerDropdown
                  manager={creator.manager}
                  title="Talent Manager"
                  isShared={isShared as boolean}
                />
              )}

              <DropdownDivider />
              <AddNoteDropdownItem
                onNoteOpen={onNoteOpen}
                note={note}
                isShared={isShared}
              />

              {!list.name.match(/Roster$/) && (
                <RemoveCreatorDropdownItem
                  onRemoveFromList={handleRemoveFromList}
                />
              )}
            </DropdownContent>
          </>
        )}
      </Dropdown>
    </>
  );
}
